export type MenuArea = 'top' | 'overflow' | 'bottom';

export interface MenuItemSlot {
  id: string;
}

export interface MenuSeparatorSlot {
  separator: true;
}

export type MenuSlot = MenuItemSlot | MenuSeparatorSlot;

export interface MainMenuLayout {
  top: MenuSlot[];
  overflow: MenuSlot[];
  bottom: MenuSlot[];
}

export interface MenuRow {
  id: string;
  /** Source index of the separator that follows this row, or null when there is none. */
  separatorAfter: number | null;
}

export const MENU_AREAS: readonly MenuArea[] = ['top', 'overflow', 'bottom'];

export function isItemSlot(slot: MenuSlot): slot is MenuItemSlot {
  return 'id' in slot;
}

function isSeparatorSlot(slot: MenuSlot | undefined): slot is MenuSeparatorSlot {
  return !!slot && 'separator' in slot && slot.separator === true;
}

function isStoredLayout(value: unknown): value is MainMenuLayout {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<Record<MenuArea, unknown>>;
  return MENU_AREAS.every(
    (area) =>
      Array.isArray(candidate[area]) &&
      candidate[area].every(
        (slot) =>
          !!slot &&
          typeof slot === 'object' &&
          (('id' in slot && typeof slot.id === 'string') ||
            ('separator' in slot && slot.separator === true)),
      ),
  );
}

function normalizeSequence(sequence: MenuSlot[], knownIds: ReadonlySet<string>): MenuSlot[] {
  const normalized: MenuSlot[] = [];

  for (const slot of sequence) {
    if (isItemSlot(slot)) {
      if (knownIds.has(slot.id)) normalized.push({ id: slot.id });
      continue;
    }

    if (normalized.length > 0 && !isSeparatorSlot(normalized[normalized.length - 1])) {
      normalized.push({ separator: true });
    }
  }

  return normalized;
}

function cloneLayout(layout: MainMenuLayout): MainMenuLayout {
  return {
    top: layout.top.map((slot) => ({ ...slot })),
    overflow: layout.overflow.map((slot) => ({ ...slot })),
    bottom: layout.bottom.map((slot) => ({ ...slot })),
  };
}

export function reconcileLayout(
  stored: unknown,
  knownIds: ReadonlySet<string>,
  defaults: MainMenuLayout,
): MainMenuLayout {
  if (!isStoredLayout(stored)) {
    return {
      top: normalizeSequence(defaults.top, knownIds),
      overflow: normalizeSequence(defaults.overflow, knownIds),
      bottom: normalizeSequence(defaults.bottom, knownIds),
    };
  }

  const seen = new Set<string>();
  const reconciled = cloneLayout(stored);

  for (const area of MENU_AREAS) {
    reconciled[area] = normalizeSequence(reconciled[area], knownIds).filter((slot) => {
      if (!isItemSlot(slot)) return true;
      if (seen.has(slot.id)) return false;
      seen.add(slot.id);
      return true;
    });
  }

  for (const area of MENU_AREAS) {
    for (const slot of defaults[area]) {
      if (isItemSlot(slot) && knownIds.has(slot.id) && !seen.has(slot.id)) {
        reconciled[area].push({ id: slot.id });
        seen.add(slot.id);
      }
    }
  }

  for (const area of MENU_AREAS) {
    reconciled[area] = normalizeSequence(reconciled[area], knownIds);
  }

  return reconciled;
}

/**
 * Turns a stored sequence into renderable rows, attaching each separator to the row it follows.
 * Separators that would end up leading or doubled once absent items are filtered out are dropped.
 */
export function renderRows(sequence: MenuSlot[], liveIds: ReadonlySet<string>): MenuRow[] {
  const rows: MenuRow[] = [];

  sequence.forEach((slot, sourceIndex) => {
    if (isItemSlot(slot)) {
      if (liveIds.has(slot.id)) rows.push({ id: slot.id, separatorAfter: null });
      return;
    }

    const previous = rows[rows.length - 1];
    if (previous && previous.separatorAfter === null) previous.separatorAfter = sourceIndex;
  });

  return rows;
}

export function moveMenuItem(
  layout: MainMenuLayout,
  itemId: string,
  destination: MenuArea,
  liveIndex: number,
  liveIds: ReadonlySet<string>,
  knownIds: ReadonlySet<string>,
): MainMenuLayout {
  const next = cloneLayout(layout);

  for (const area of MENU_AREAS) {
    next[area] = next[area].filter((slot) => !isItemSlot(slot) || slot.id !== itemId);
  }

  const target = next[destination];
  const liveItemPositions = target
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => isItemSlot(slot) && liveIds.has(slot.id))
    .map(({ index }) => index);

  let insertionIndex: number;
  if (liveIndex < liveItemPositions.length) {
    insertionIndex = liveItemPositions[liveIndex];
  } else {
    insertionIndex = target.length;
    while (insertionIndex > 0 && isSeparatorSlot(target[insertionIndex - 1])) insertionIndex--;
  }

  target.splice(insertionIndex, 0, { id: itemId });

  for (const area of MENU_AREAS) {
    next[area] = normalizeSequence(next[area], knownIds);
  }

  return next;
}

export function addSeparator(layout: MainMenuLayout, area: MenuArea, afterItemId: string): MainMenuLayout {
  const index = layout[area].findIndex((slot) => isItemSlot(slot) && slot.id === afterItemId);

  if (index < 0 || isSeparatorSlot(layout[area][index + 1])) return layout;
  const next = cloneLayout(layout);
  next[area].splice(index + 1, 0, { separator: true });
  return next;
}

export function removeSeparator(layout: MainMenuLayout, area: MenuArea, sourceIndex: number): MainMenuLayout {
  if (!isSeparatorSlot(layout[area][sourceIndex])) return layout;
  const next = cloneLayout(layout);
  next[area].splice(sourceIndex, 1);
  return next;
}
